import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { useRegisterHRMutation, useEditHRMutation } from "@/store/hrAuth";
import { Eye, EyeOff } from "lucide-react";

// Modified HRForm component to handle both creation and editing
const HRForm = ({ hrData, onClose, mode = "create" }) => {
  const [registerHR] = useRegisterHRMutation();
  const [updateHR] = useEditHRMutation();
  const [showPassowrd, setShowPassword] = React.useState(false);

  const [formData, setFormData] = React.useState({
    name: hrData?.name || "",
    email: hrData?.email || "",
    password: "",
    role: "hr",
  });

  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState({ message: "", type: "" });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (mode === "create" && !formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ message: "", type: "" });

    if (validateForm()) {
      try {
        if (mode === "edit") {
          // Only include password in update if it was changed
          const updateData = {
            hrData: formData,
            hrId: hrData._id,
          };
          if (!formData.password) {
            delete updateData.password;
          }
          await updateHR(updateData).unwrap();
          setStatus({
            type: "success",
            message: "HR updated successfully!",
          });
        } else {
          await registerHR(formData).unwrap();
          setStatus({
            type: "success",
            message: "Registration successful!",
          });
        }

        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        setStatus({
          type: "error",
          message: error.message || "Operation failed",
        });
      }
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          <h1>{mode === "edit" ? "Edit HR" : "HR Registration"}</h1>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              Password{" "}
              {mode === "edit" && "(Leave blank to keep current password)"}
            </Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassowrd ? "text" : "password"}
                placeholder={
                  mode === "edit"
                    ? "Enter new password (optional)"
                    : "Enter password"
                }
                value={formData.password}
                onChange={handleInputChange}
                className={errors.password ? "border-red-500" : ""}
              />
              {showPassowrd ? (
                <Eye
                  className="absolute right-4 top-2 size-5 cursor-pointer"
                  onClick={() => setShowPassword(false)}
                />
              ) : (
                <EyeOff
                  className="absolute right-4 top-2 size-5 cursor-pointer"
                  onClick={() => setShowPassword(true)}
                />
              )}
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          {status.message && (
            <Alert
              className={`mb-4 ${
                status.type === "success" ? "bg-teal-50" : "bg-red-50"
              }`}
            >
              <AlertDescription>{status.message}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="w-full">
            {mode === "edit" ? "Update" : "Register"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default HRForm;
